// The server counts captions in Unicode code points (Go utf8.RuneCountInString), not UTF-16 units.

export const codePointCount = (s: string): number => Array.from(s).length;

/** Cut to `max` code points, never splitting a surrogate pair. */
export function cutToCodePoints(s: string, max: number): string {
  const cps = Array.from(s);
  return cps.length > max ? cps.slice(0, max).join("") : s;
}

const isHigh = (c: number) => c >= 0xd800 && c <= 0xdbff;
const isLow = (c: number) => c >= 0xdc00 && c <= 0xdfff;

/**
 * Like a native maxLength, but in code points: when an edit turns `prev` into `next` (caret right
 * after the inserted run) and `next` is over `max`, the existing text stays and only the inserted
 * run is cut, never splitting a surrogate pair. Returns the value to keep and where the caret goes.
 */
export function clampInsertion(prev: string, next: string, caret: number, max: number): { value: string; caret: number } {
  if (codePointCount(next) <= max) return { value: next, caret };
  // The kept tail: the text after the caret, if it matches the end of `prev`; else the common suffix.
  let tailLen = next.length - caret;
  if (caret < 0 || tailLen < 0 || tailLen > prev.length || !prev.endsWith(next.slice(caret))) {
    tailLen = 0;
    while (tailLen < prev.length && tailLen < next.length && prev[prev.length - 1 - tailLen] === next[next.length - 1 - tailLen]) tailLen++;
  }
  if (tailLen > 0 && isLow(next.charCodeAt(next.length - tailLen))) tailLen--;
  let head = 0;
  const headMax = Math.min(prev.length, next.length) - tailLen;
  while (head < headMax && prev[head] === next[head]) head++;
  if (head > 0 && isHigh(next.charCodeAt(head - 1))) head--;
  const end = next.length - tailLen;
  const room = max - codePointCount(next.slice(0, head)) - codePointCount(next.slice(end));
  const kept = room > 0 ? cutToCodePoints(next.slice(head, end), room) : "";
  return { value: next.slice(0, head) + kept + next.slice(end), caret: head + kept.length };
}
